import { html, LitElement, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { Balance } from '../../_ui-core_/base/package/api/package/types';
import { VideoOrchestrator } from '../../_ui-core_/base/package/controller/package/video-orchestrator';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { FeatureFlag } from '../../_ui-core_/base/package/services/package/features/feature-service';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../_ui-core_/base/package/util/a11y';
import { isMobileWidth, LayoutType, layoutType } from '../../_ui-core_/base/package/util/device';
import {
  updateCanonicalUrl,
  updateMetaDescription,
  updateMetaTitle,
} from '../../_ui-core_/base/package/util/html-helper';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import { formatMoney } from '../../_ui-core_/base/package/util/money';
import { reduceMotionStore } from '../../_ui-core_/base/package/util/reduce-motion';
import { idleTick, paintTicks } from '../../_ui-core_/base/package/util/render';
import { isWithinGermanSchedule } from '../../_ui-core_/base/package/util/time';
import { UiPaymentTeaserModal } from '../../_ui-core_/components/drawers/payment-teaser-modal/ui-payment-teaser-modal';
import { NavBarIconName } from '../../_ui-core_/components/nav-bar/ui-nav-bar-icon';
import type { SEOText } from '../../_ui-core_/components/ui-footer/ui-footer';
import type { UIGameTile } from '../../_ui-core_/components/ui-game-tile/ui-game-tile';
import { openGamification, showGamificationButton } from '../../_ui-core_/mod-incentive/helper/gamification-launcher';
import App from '../../app';
import type {
  Footer,
  Link,
  SeoFAQ,
  StaticPage,
  WelcomeOfferConfig,
} from '../../app/package/base/cms/package/cms-domain';
import { MainRoute } from '../../app/package/base/router/router';
import type { BaseTemplate } from '../../app/package/base/router/router-types/page-template-types';
import {
  type ShowSidebarEvent,
  type SidebarEvent,
  SidebarEventName,
  SidebarEventType,
} from '../../app/package/base/router/router-types/sidebar-types';
import { type LoginObject, LoginStatus } from '../../app/package/base/service/login/login-domain';
import { ModalType } from '../../app/package/base/service/modals/modal-service';
import { ElementId } from '../../app/package/base/service/onboarding/onboarding-service';
import { Product } from '../../app/package/base/service/product/product-domain';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import { LowBalanceFlyerController } from '../../components/controller/low-balance-flyer-controller';
import { STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE } from '../../constants';
import grid from '../base-grid.css?inline';
import { type BasePageHeaderOptions, BasePageHeaderType } from './base-page-header';
import './base-page-sidebar';
import style from './base-page.css?inline';
import '../../_ui-core_/components/layout/ui-layout-bar/ui-layout-bar';
import '../../_ui-core_/components/nav-bar/ui-gamification-icon';
import '../../_ui-core_/components/ui-lugas-session-timer/ui-lugas-session-timer';
import '../../_ui-core_/components/ui-footer/ui-footer';
import '../../_ui-core_/components/ui-spinner/ui-spinner';

const CName = 'base-page';

interface MobileBottomBarOptions {
  detail: TemplateResult;
}

/**
 * Base page component
 * @element base-page
 * @slot - Default slot for page content
 * @prop {BasePageHeaderType} headerType - Header type
 * @prop {BasePageHeaderOptions} headerOptions - Header options
 * @prop {Boolean} isMobileHeaderShown - Show header. Affects mobile layout only
 * @prop {Boolean} isFooterShown - Show footer
 * @prop {Boolean} isFullScreen - Full screen mode. Render without desktop sidebar
 * @prop {Boolean} showBottomBar - Show bottom bar
 */
@customElement(CName)
export class BasePage extends LitElement {
  static readonly styles = [unsafeCSS(grid), unsafeCSS(style)];

  @property({ type: String }) headerType: BasePageHeaderType;
  @property({ type: Object }) mobileNavigationBarOptions?: MobileBottomBarOptions;
  @property({ type: Boolean }) isMobileHeaderShown = true;
  @property({ type: Boolean }) isFooterShown = true;
  @property({ type: Boolean }) isFullScreen = false;
  @property({ type: Boolean }) showBottomBar = true;

  #videoOrchestrator?: VideoOrchestrator;

  @state() private _a11yWidgetEnabled = false;
  @state() private _balance: string;
  @state() private _currentRouteName = App.instance.router.getRouteName();
  @state() private _footerConfig?: Footer = undefined;
  @state() private _footerSeoFaq: SeoFAQ[] = [];
  @state() private _footerSeoText: SEOText = { base: '', extended: '' };
  @state() private _headerOptions: BasePageHeaderOptions;
  @state() private _isHostGapHidden = false;
  @state() private _isSidebarOpen = App.instance.router.getIsSidebarOpen();
  @state() private _loginStatus: LoginStatus;
  @state() private _shouldRenderGamificationIcon = false;
  @state() private _showBalanceAsActive = false;
  @state() private _showRenderedContent = false;
  @state() private _sidebarHeaderType: BasePageHeaderType;
  @state() private _sidebarView: TemplateResult | typeof nothing;

  private _welcomeOfferOverride?: { title: string; subtitle: string };
  private _welcomeOfferDesktopOverride?: { title: string; subtitle: string };

  private readonly _layoutType = layoutType();
  private readonly _subHelper = new SubHelper();
  private _clearSidebarScheduled = false;
  private _prerenderTask: number | null = null;
  private _lowBalanceFlyerController?: LowBalanceFlyerController;

  private readonly _handleSidebarEvent = async (ev: Event) => {
    this._isSidebarOpen = App.instance.router.getIsSidebarOpen();
    const type = (ev as CustomEvent<SidebarEvent>).detail?.type;
    if (type === SidebarEventType.SHOW) {
      // Sidebar show event
      this._clearSidebarScheduled = false;
      this._handleSetSidebarEvent(ev);
    } else if (type === SidebarEventType.CLEAR) {
      // Schedule sidebar clear event
      this._clearSidebarScheduled = true;
      await paintTicks(2);
      if (this._clearSidebarScheduled) {
        // Clear sidebar
        this._handleClearSidebarEvent();
      }
    } else {
      window.$app.logger.log('sidebar event type not supported:', type);
    }
  };

  private readonly _handleSetSidebarEvent = (ev: Event) => {
    const { view, pageTemplate } = (ev as CustomEvent<SidebarEvent>).detail as ShowSidebarEvent;
    this._sidebarHeaderType = (pageTemplate as BaseTemplate).options?.headerType ?? BasePageHeaderType.DEFAULT;
    this._sidebarView = view;
  };

  private readonly _handleClearSidebarEvent = () => {
    window.$app.logger.log('handle clear sidebar event');
    this._closeSidebar();
  };

  connectedCallback() {
    super.connectedCallback();
    this._prerenderTask = window.$app.prerenderService.addTask('base-page-SEO');
    this._subHelper.addSub(window.$app.loginStore, (_) => this._updateLogin(_), true);
    this._subHelper.addSub(App.instance.balanceStore, (_) => this._updateBalance(_), true);

    this._addSidebarEventListener();
    this._getCasinoConfig();
    this._initVideoOrchestrator();
    this._subscribeToRouter();
    this._shouldRenderGamificationIcon = showGamificationButton();
    if (!this._shouldRenderGamificationIcon) {
      window.$app.onboarding.skipElement(ElementId.GAMIFICATION_ICON_MOBILE);
    }
    window.$app.features.hasFeature(FeatureFlag.A11yWidgetEnabled).then((isEnabled) => {
      if (!this.isConnected) return;
      this._a11yWidgetEnabled = isEnabled;
      if (isEnabled) {
        import('@tipico/a11y-widget');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
    this.#videoOrchestrator?.disconnect();
    this._lowBalanceFlyerController?.destroy();
    this._removeSidebarEventListener();
    window.$app.onboarding.unregisterElement(ElementId.DEPOSIT_ICON);
    window.$app.onboarding.unregisterElement(ElementId.GAMIFICATION_ICON_MOBILE);
  }

  firstUpdated(): void {
    this._subHelper.addSub(
      window.$app.renderService.renderStore,
      (renderStatus) => {
        this._showRenderedContent = renderStatus === 'visible';
        if (renderStatus === 'visible') {
          this._updateSEOHtmlContent();
          idleTick().then(() => {
            if (!reduceMotionStore.value) {
              this.#videoOrchestrator?.startPlayback();
            }
            this._lowBalanceFlyerController?.init();
          });
        }
      },
      true,
    );
    this._subHelper.addSub(
      App.instance.visibilityStore,
      (_) => this._handleVideoOrchestratorOnVisibilityChange(_),
      true,
    );
    this._subHelper.addSub(reduceMotionStore, (reduceMotion) => {
      if (reduceMotion) {
        this.#videoOrchestrator?.stopPlayback();
      } else {
        this.#videoOrchestrator?.startPlayback();
      }
    });

    const depositIcon = this.shadowRoot?.getElementById(ElementId.DEPOSIT_ICON);
    if (depositIcon) {
      window.$app.onboarding.registerElement(ElementId.DEPOSIT_ICON, depositIcon);
    }

    window.$app.renderService.removePageTemplateTask();
  }

  updated() {
    const gamificationIcon = this.shadowRoot?.getElementById(ElementId.GAMIFICATION_ICON_MOBILE);
    if (gamificationIcon && !window.$app.onboarding.hasElement(ElementId.GAMIFICATION_ICON_MOBILE)) {
      window.$app.onboarding.registerElement(ElementId.GAMIFICATION_ICON_MOBILE, gamificationIcon);
    } else if (!gamificationIcon && window.$app.onboarding.hasElement(ElementId.GAMIFICATION_ICON_MOBILE)) {
      window.$app.onboarding.unregisterElement(ElementId.GAMIFICATION_ICON_MOBILE);
    }
  }

  render() {
    const classes = classMap({
      root: true,
      'root--gradient': isMobileWidth(),
      'fixate-header-and-footer': !this._showRenderedContent && isMobileWidth(),
      isFullScreen: this.isFullScreen,
      'hide-base-page-gap': this._isHostGapHidden,
    });
    return html`
      ${this._a11yWidgetEnabled ? html`<a11y-widget dark-mode font-size high-contrast reduce-motion hide-widget></a11y-widget>` : nothing}
      <div
        class=${classes}
        data-route=${App.instance.router.getRouteName()}
        @header-options-update=${this._handleHeaderUpdate}
        @updateMobileBottomBar=${this._handleUpdateMobileBottomBar}
      >
        ${this._layoutType === LayoutType.DESKTOP ? this._renderDesktopLayout() : this._renderMobileLayout()}
      </div>
    `;
  }

  // ———————————————————— Mobile layout ————————————————————

  private _renderLugasTimer(): TemplateResult<1> | typeof nothing {
    return this._loginStatus === LoginStatus.LOGGED_IN
      ? html`
          <div class="lugas-timer-panel">
            <ui-lugas-session-timer></ui-lugas-session-timer>
          </div>
        `
      : nothing;
  }

  private _renderMobileLayout() {
    return html`
      ${this.isMobileHeaderShown ? this._renderMobileHeader() : nothing} ${this._renderSlot()}
      ${this._renderFooter()}
      ${this.showBottomBar ? this._renderMobileBottomBar() : nothing} ${this._renderLugasTimer()}
    `;
  }

  private _renderMobileHeader() {
    return html` <base-page-header .type=${this.headerType} .options=${this._headerOptions}></base-page-header> `;
  }

  private _renderMobileBottomBar() {
    if (this.mobileNavigationBarOptions?.detail) {
      return html`${this.mobileNavigationBarOptions?.detail}`;
    }

    return this._renderMobileBottomNavigation();
  }

  private _renderMobileBottomNavigation() {
    const hasUserLoggedInOnce = localStorage_getOrNull(STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE);
    const barClasses = classMap({
      'bottom-bar': true,
      backdrop: true,
      hidden: this._loginStatus === LoginStatus.UNDEFINED || this._loginStatus === LoginStatus.FETCHING,
    });
    const isHomeRoute = App.instance.router.isHomeRoute();
    const isSearchRoute = this._currentRouteName === MainRoute.SEARCH;
    const isPromotionRoute = this._currentRouteName === this._promotionsSlug;
    return html`
      <ui-layout-bar class=${barClasses}>
        ${
          this._loginStatus === LoginStatus.LOGGED_OUT && !hasUserLoggedInOnce
            ? this._renderMobileBottomPromo()
            : nothing
        }
        <nav role="navigation" aria-label=${window.$app.$t.get(I18n.a11y.heading.navigation)} slot="center" class="nav max-size" style="overflow: visible">
          <ui-nav-bar-icon
            @click=${() => this._handleMobileBottomBarClick(NavBarIconName.HOME)}
            ?active=${isHomeRoute}
            aria-current=${isHomeRoute ? 'page' : undefined}
            .icon=${NavBarIconName.HOME}
            .href=${App.instance.router.getPathNavigateToHome()}
            label=${window.$app.$t.get(I18n.navigation.navbar.home)}
            .isA11yMainNavHome=${true}
          ></ui-nav-bar-icon>
          <ui-nav-bar-icon
            @click=${() => {
              if (App.instance.featureFlag.allowPromos()) {
                this._handleMobileBottomBarClick(NavBarIconName.PROMO);
              }
            }}
            class=${App.instance.featureFlag.allowPromos() ? '' : 'disabled'}
            ?active=${isPromotionRoute}
            aria-current=${isPromotionRoute ? 'page' : undefined}
            .icon=${NavBarIconName.PROMO}
            .href=${App.instance.router.getPathNavigateToPromotions()}
            label=${window.$app.$t.get(I18n.navigation.navbar.promo)}
          ></ui-nav-bar-icon>
          ${this._shouldRenderGamificationIcon ? this._renderGamificationIcon() : nothing}
          <ui-nav-bar-icon
            @click=${() => this._handleMobileBottomBarClick(NavBarIconName.SEARCH)}
            ?active=${isSearchRoute}
            aria-current=${isSearchRoute ? 'page' : undefined}
            .icon=${NavBarIconName.SEARCH}
            .href=${App.instance.router.getPathNavigateToSearch()}
            label=${window.$app.$t.get(I18n.navigation.navbar.search)}
          ></ui-nav-bar-icon>
          ${this._renderDepositButton()}
        </nav>
      </ui-layout-bar>
    `;
  }

  private _renderGamificationIcon(): TemplateResult {
    if (this._loginStatus === LoginStatus.UNDEFINED || this._loginStatus === LoginStatus.FETCHING) {
      return html`<ui-spinner style="margin-block:auto" class="s"></ui-spinner>`;
    }

    if (!App.instance.featureFlag.allowMissions()) {
      return html`
        <div id=${ElementId.GAMIFICATION_ICON_MOBILE}>
          <ui-gamification-icon aria-hidden="true" ?isActive=${false} class="disabled"></ui-gamification-icon>
        </div>
      `;
    }

    return html`
      <div id=${ElementId.GAMIFICATION_ICON_MOBILE}>
        <ui-gamification-icon
          tabindex="0"
          role="button"
          aria-label=${window.$app.$t.get(I18n.navigation.navbar.missions)}
          data-testid="gamification-button"
          @click=${() => this._handleGamificationButtonClick()}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleGamificationButtonClick.bind(this))}
          ?isActive=${this._loginStatus === LoginStatus.LOGGED_IN}
        ></ui-gamification-icon>
      </div>
    `;
  }

  private _renderDepositButton(): TemplateResult {
    const label =
      this._loginStatus === LoginStatus.LOGGED_IN ? this._balance : window.$app.$t.get(I18n.navigation.navbar.deposit);

    if (App.instance.featureFlag.showPayments()) {
      return html`
        <ui-nav-bar-icon
          id=${ElementId.DEPOSIT_ICON}
          tabindex="0"
          @click=${(e: Event) => {
            if (this._loginStatus === LoginStatus.LOGGED_IN) {
              this._handleMobileBottomBarClick(NavBarIconName.DEPOSIT);
            } else {
              this._renderPaymentTeaserModal(e);
            }
          }}
          ?active=${this._showBalanceAsActive}
          .icon=${NavBarIconName.DEPOSIT}
          label=${label}
        ></ui-nav-bar-icon>
      `;
    }

    return html` <ui-nav-bar-icon class="disabled" .icon=${NavBarIconName.WALLET} label=${label}></ui-nav-bar-icon> `;
  }

  private _renderMobileBottomPromo() {
    const title = this._welcomeOfferOverride?.title ?? window.$app.$t.get(I18n.base.welcomeOffer.mobile.title);
    const subtitle = this._welcomeOfferOverride?.subtitle ?? window.$app.$t.get(I18n.base.welcomeOffer.mobile.subtitle);
    return html`
      <aside slot="top" class="promotion" aria-label=${window.$app.$t.get(I18n.base.welcomeOffer.mobile.title)}>
        <div class="welcome-offer">
          <div class="welcome-offer--content">
            <div class="welcome-offer-icon">
              <ui-bonus-navigation-icon></ui-bonus-navigation-icon>
            </div>
            <div class="welcome-offer--text">
              <p class="title">${unsafeHTML(title)}</p>
              <p class="subtitle">${subtitle}</p>
            </div>
          </div>
          <ui-btn
            variant="secondary"
            .isBlock=${true}
            @click=${() => App.instance.product.gotoSignup(TrackingEventSource.REGISTRATION_BANNER)}>
              ${window.$app.$t.get(I18n.base.register)}
          </ui-btn>
        </div>
      </aside>
    `;
  }

  private _renderPaymentTeaserModal(e: Event) {
    if (!window.$app.appConfig.paymentTeaserItems.length) {
      window.$app.logger.warn(
        'Payment teaser modal not shown: no paymentTeaserItems configured (check casino config paymentTeaserOptions)',
      );
      return;
    }
    window.$app.track.menuNavigation({ eventAction: 'deposit', navMode: 'normalMode' });
    const modal = new UiPaymentTeaserModal();
    modal.trackingSource = TrackingEventSource.BOTTOM_NAV_DEPOSIT;
    window.$app.modal.show(modal, ModalType.PaymentTeaser, e.target as HTMLElement);
  }

  // ———————————————————— Desktop layout ————————————————————

  private _renderDesktopLayout() {
    return html`
      ${this._renderDesktopSidebar()} ${this._renderSecondaryHeader()} ${this._renderSlot()}
      ${this._renderFooter()}
    `;
  }

  private _renderDesktopSidebar() {
    // In full-screen mode (game page), only mount the sidebar when a slide-in
    // view is active (e.g. account panel opened from the game header).
    if (this.isFullScreen && !this._sidebarView) return nothing;

    const classes = classMap({
      'desktop-sidebar': true,
      isFullScreen: this.isFullScreen,
    });

    return html`
      <base-page-sidebar
        tabindex=${this.isFullScreen ? -1 : undefined}
        class=${classes}
        .balance=${this._balance}
        .loginStatus=${this._loginStatus}
        .slideinView=${this._sidebarView}
        .headerType=${this._sidebarHeaderType}
        .currentRouteName=${this._currentRouteName}
        .slideInOnly=${this.isFullScreen}
        .welcomeOfferOverride=${this._welcomeOfferDesktopOverride}
        @navigate-account=${this._handleAccountClick}
        @navigate-deposit=${this._handleDepositClick}
        @navigate-home=${this._handleHomeClick}
        @navigate-login=${this._handleLoginClick}
        @navigate-search=${this._handleSearchClick}
        @navigate-sports=${this._handleSportsClick}
        @navigate-games=${this._handleGamesClick}
        @navigate-to=${this._handleCustomPathClick}
        @open-game=${(ev: { detail: { refId: string } }) => this._openGameFromWidget(ev.detail.refId)}
      ></base-page-sidebar>
    `;
  }

  /**
   * This header is used for the header in the slide-in on desktop
   */
  private _renderSecondaryHeader() {
    return html`
      <base-page-header
        type=${this.headerType}
        .options=${this._headerOptions}
        .secondaryHeader=${true}
      ></base-page-header>
    `;
  }

  // ——————————————————————————————————————————————————————————————————

  private _renderFooter() {
    if (!this._footerConfig) return nothing;

    return html`
      <ui-footer
        ?inert=${this._isSidebarOpen || !this.isFooterShown}
        ?hidden=${!this.isFooterShown}
        class="footer ${this._showRenderedContent ? 'show' : ''}"
        .seoText=${{ ...this._footerSeoText }}
        .seoFaq=${this._footerSeoFaq}
        .footerConfig=${this._footerConfig}
        .isUserLoggedIn=${this._loginStatus === LoginStatus.LOGGED_IN}
      ></ui-footer>
    `;
  }

  private _renderSlot() {
    return html`
      <slot
        ?inert=${this._isSidebarOpen}
        class="main-slot ${this._showRenderedContent ? 'show' : ''}"
        @restore-scroll-position=${(ev: Event) => {
          ev.stopPropagation();
          App.instance.router.restoreScrollPosition();
        }}
        @register-playable=${this._onRegisterPlayable}
        @video-ended=${this._onVideoEnded}
        @open-game=${(ev: { detail: { refId: string } }) => this._openGameFromWidget(ev.detail.refId)}
      ></slot>
    `;
  }

  private get _promotionsSlug() {
    return App.instance.featureFlag.promotionsSlug;
  }

  private _enrichFooterConfig() {
    if (!this._footerConfig) {
      return;
    }
    this._footerConfig.responsibleGaming.text = App.instance.appSettings.isSHCasino
      ? I18n.sh.footer.responsibleGaming
      : I18n.footer.responsibleGaming;
    this._footerConfig.helpCenterText = App.instance.appSettings.isSHCasino
      ? I18n.sh.footer.helpCenterText
      : I18n.footer.helpCenterText;

    App.instance.content.getStaticPages().then((pages) => {
      if (!this._footerConfig) return;
      const staticPageFooterLinks: Link[] = pages
        .filter((page) => page.footerLabel)
        .map((page) => {
          const isExternal = page.path.startsWith('http');
          return {
            title: page.footerLabel!,
            nav: page.path,
            followEnabled: false,
            sameSite: isExternal ? false : undefined,
          };
        });
      if (staticPageFooterLinks.length > 0) {
        const staticPagePaths = new Set(staticPageFooterLinks.map((link) => link.nav));
        const existingLinks = this._footerConfig.links.filter((link) => !staticPagePaths.has(link.nav));
        this._footerConfig = {
          ...this._footerConfig,
          links: [...existingLinks, ...staticPageFooterLinks],
        };
      }
    });
  }

  private _getCasinoConfig() {
    const config = window.$app.casinoConfig;
    if (config.footer) {
      this._footerConfig = config.footer;
    }
    this._enrichFooterConfig();
    this._welcomeOfferOverride = this._resolveWelcomeOfferOverride(config.welcomeOffer, 'mobile');
    this._welcomeOfferDesktopOverride = this._resolveWelcomeOfferOverride(config.welcomeOffer, 'desktop');
    // Construction is cheap (stores config only); init() kicks off subscriptions and a
    // PAM has-deposit fetch, so it's deferred to the idle-gated section in firstUpdated()
    // to avoid competing with boot-critical requests (mirrors Application._initActivationFlow).
    this._lowBalanceFlyerController = new LowBalanceFlyerController(config.lowBalanceFlyer);
  }

  private _resolveWelcomeOfferOverride(
    config: WelcomeOfferConfig | undefined,
    variant: 'desktop' | 'mobile',
  ): { title: string; subtitle: string } | undefined {
    if (!config) return undefined;
    if (!isWithinGermanSchedule(config.startDatetime, config.endDatetime)) return undefined;
    return config[variant];
  }

  private _removePrerenderTask(): void {
    idleTick().then(() => {
      if (this._prerenderTask !== null) {
        this._prerenderTask = window.$app.prerenderService.removeTask(this._prerenderTask);
      }
    });
  }

  private _subscribeToRouter() {
    this._subHelper.addSub(
      App.instance.routerStore,
      (value) => {
        this._currentRouteName = value.url;
        // route.name from routerStore includes the root prefix (e.g. 'de/game/...'),
        // unlike getCurrentLocation() which strips it
        this._isHostGapHidden = MainRoute.GAME === value.route.name.split('/')[1];
        // Reset stale footer SEO data immediately to prevent flicker (IGM-3930)
        this._footerSeoFaq = [];
        this._footerSeoText = { base: '', extended: '' };
        this._updateSEOHtmlContent();
        // Reset the video orchestrator on navigation to release references to
        // game tiles from the previous page (IGM-3644).
        this.#videoOrchestrator?.unobserveAll();
      },
      true,
    );
  }

  private async _updateSEOHtmlContent() {
    if (new URL(window.location.href).pathname.includes(MainRoute.HELP_CENTER)) {
      updateCanonicalUrl(window.$app.appConfig.helpCenterIframeUrl);
    } else {
      updateCanonicalUrl();
    }
    this._updateFooterSeoData();
    const isDone = await this._fetchPathSpecificSEO();
    if (isDone) {
      this._removePrerenderTask();
    }
  }

  private async _fetchPathSpecificSEO(): Promise<boolean> {
    const res = await App.instance.content.getSEOHtmlContent(App.instance.routerStore.value.url);

    // If response is not available yet it's undefined. It was triggered already and will be processed later.
    if (res === undefined) {
      return false;
    }

    // Manual CMS values
    const manualTitle = res?.content?.metaTitle;
    const manualDesc = res?.content?.metaDescription;

    // Dynamic game-info values
    const isGameInfo = App.instance.router.isGameInfoRoute();
    const dynamicTitle = this._headerOptions?.title;
    const dynamicProvider = this._headerOptions?.provider;
    const fallbackTitle = window.$app.appConfig.metaTitle;

    // Title
    if (manualTitle) {
      updateMetaTitle(manualTitle);
    } else if (isGameInfo && dynamicTitle) {
      const metaTitleTemplate = App.instance.appSettings.isSHCasino
        ? I18n.sh.gameInfo.seoMetaTitle
        : I18n.gameInfo.seoMetaTitle;
      updateMetaTitle(window.$app.$t.get(metaTitleTemplate, { title: dynamicTitle }));
    } else if (!App.instance.router.isGamePageOpen() && !(await this._isStaticPageRoute())) {
      updateMetaTitle(fallbackTitle);
    }

    // Description
    if (manualDesc) {
      updateMetaDescription(manualDesc);
    } else if (isGameInfo && dynamicTitle && dynamicProvider) {
      const metaDescTemplate = App.instance.appSettings.isSHCasino
        ? I18n.sh.gameInfo.seoMetaDescription
        : I18n.gameInfo.seoMetaDescription;
      updateMetaDescription(
        window.$app.$t.get(metaDescTemplate, {
          title: dynamicTitle,
          provider: dynamicProvider,
        }),
      );
    }

    return true;
  }

  private async _isStaticPageRoute(): Promise<boolean> {
    return (await this._getStaticPageForCurrentRoute()) !== undefined;
  }

  private async _getStaticPageForCurrentRoute(): Promise<StaticPage | undefined> {
    const currentPath = App.instance.routerStore.value.url;
    const pages = await App.instance.content.getStaticPages();
    return pages.find((page) => page.path.replace(/^\//, '') === currentPath);
  }

  private async _updateFooterSeoData() {
    let footerText: string | undefined;
    let faq: SeoFAQ[] | undefined;

    const staticPage = await this._getStaticPageForCurrentRoute();
    if (staticPage) {
      faq = staticPage.seo.faq ?? undefined;
    } else {
      const res = await App.instance.content.getSEOHtmlContent(App.instance.routerStore.value.url);
      if (res === undefined) {
        return;
      }
      footerText = res?.content?.footerText;
      faq = res?.content?.faq;
    }

    // Only update if changed
    const footerSeoText = this._splitFooterText(footerText);
    const footerSeoFaq = faq ?? [];
    if (
      this._footerSeoText.base === footerSeoText.base &&
      this._footerSeoText.extended === footerSeoText.extended &&
      JSON.stringify(this._footerSeoFaq) === JSON.stringify(footerSeoFaq)
    ) {
      window.$app.logger.log('Footer SEO data unchanged.');
      return;
    }

    this._footerSeoText = footerSeoText;
    this._footerSeoFaq = footerSeoFaq;
  }

  private _openGameFromWidget(gameId: string) {
    App.instance.router.navigateToGame(true, gameId);
  }

  private _closeSidebar() {
    this._sidebarView = nothing;
  }

  private _handleHeaderUpdate(ev: Event) {
    if (App.instance.router.getIsSidebarOpen()) {
      return;
    }
    ev.stopPropagation();
    window.$app.logger.log('handle update header event', (ev as CustomEvent<BasePageHeaderOptions>).detail);
    this._headerOptions = (ev as CustomEvent<BasePageHeaderOptions>).detail;
    this._updateSEOHtmlContent();
  }

  private _handleUpdateMobileBottomBar(ev: CustomEvent) {
    ev.stopPropagation();
    window.$app.logger.log('handle update mobile bottom bar event', ev.detail);
    this.mobileNavigationBarOptions = ev.detail;
    this.classList.toggle('incentive-bar-shown', Boolean(ev.detail?.detail));

    if (ev.detail?.detail) {
      this._publishIncentiveBarHeight();
    } else {
      document.documentElement.style.removeProperty('--incentive-bottom-bar-height');
    }

    // Reset mobileNavigationBarOptions when route changes
    const resetFn = () => {
      this.mobileNavigationBarOptions = undefined;
      this.classList.remove('incentive-bar-shown');
      document.documentElement.style.removeProperty('--incentive-bottom-bar-height');
      App.instance.routerStore.unsubscribe(resetFn);
    };
    App.instance.routerStore.subscribe(resetFn, false);
  }

  private _publishIncentiveBarHeight(): void {
    // Measure after the incentive bottom bar renders
    requestAnimationFrame(() => {
      const bar = this.shadowRoot?.querySelector('.bottom-bar.incentive');
      if (bar) {
        const height = bar.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--incentive-bottom-bar-height', `${height}px`);
      }
    });
  }

  private _handleVideoOrchestratorOnVisibilityChange(hidden: boolean) {
    if (hidden) {
      this.#videoOrchestrator?.stopPlayback();
      return;
    }

    if (!reduceMotionStore.value) {
      this.#videoOrchestrator?.startPlayback();
    }
  }

  // Catch global sidebar events
  private _addSidebarEventListener() {
    window.addEventListener(SidebarEventName, this._handleSidebarEvent);
  }

  private _removeSidebarEventListener() {
    window.removeEventListener(SidebarEventName, this._handleSidebarEvent);
  }

  private _updateLogin(val: LoginObject) {
    this._loginStatus = val.loginStatus;
  }

  private _updateBalance(balance: Balance) {
    this._balance = balance.amount === null ? 'n/a' : formatMoney(balance.amount + balance.bonus);
  }

  private _navigateToDeposit() {
    App.instance.product.gotoDeposit(false, TrackingEventSource.MOBILE_BOTTOM_NAV);
  }

  private _onVideoEnded(event: CustomEvent) {
    const element: HTMLElement | undefined = event.detail;

    if (element) {
      this.#videoOrchestrator?.playNextElement(element);
      return;
    }

    window.$app.logger.log('videoOrchestrator cannot invoke playNextElement:', element);
  }

  private _onRegisterPlayable(event: CustomEvent) {
    const element: UIGameTile | undefined = event.detail;

    if (element) {
      this.#videoOrchestrator?.addObserverTarget({
        element,
        play: () => element.playVideo(),
        stop: () => element.pauseVideo(),
      });
      return;
    }

    window.$app.logger.log('videoOrchestrator cannot register playable:', element);
  }

  private _handleHomeClick() {
    App.instance.router.navigateToHome();
    if (App.instance.router.isHomeRoute()) {
      App.instance.router.scrollToTop();
    }
  }

  private _handleLoginClick() {
    App.instance.product.gotoLogin(TrackingEventSource.DESK_LOGIN_SIDE_NAV_CTA);
  }

  private _handleSearchClick() {
    App.instance.router.navigateToSearch();
  }

  private _handleSportsClick() {
    App.instance.product.gotoProduct(Product.SPORTS, '');
  }

  private _handleGamesClick() {
    App.instance.product.gotoProduct(Product.GAMES, '');
  }

  private _handleAccountClick() {
    App.instance.router.navigateToAccount();
  }

  private _handleDepositClick() {
    App.instance.product.gotoDeposit(false, TrackingEventSource.NAV_SIDEBAR);
  }

  private _handleCustomPathClick(ev: CustomEvent<{ path: string }>) {
    App.instance.router.navigateTo(ev.detail.path);
  }

  private _initVideoOrchestrator() {
    this.#videoOrchestrator = new VideoOrchestrator({
      root: null,
      threshold: 0.8,
    });
  }

  private async _handleGamificationButtonClick() {
    window.$app.track.menuNavigation({
      eventAction: 'missions',
      navMode: App.instance.router.isGameRoute() ? 'gameMode' : 'normalMode',
    });
    await openGamification();
  }

  private _handleMobileBottomBarClick(name: NavBarIconName) {
    window.$app.track.menuNavigation({
      eventAction: name,
      navMode: 'normalMode',
    });
    // TODO: cleanup this switch statement. No default and no unnecessary cases
    switch (name) {
      case NavBarIconName.HOME:
        this._handleHomeClick();
        break;
      case NavBarIconName.DEPOSIT:
        this._navigateToDeposit();
        break;
      case NavBarIconName.PROMO:
        App.instance.router.navigateToPromotions();
        break;
      case NavBarIconName.SEARCH:
        App.instance.router.navigateToSearch();
        break;
      default:
        this._handleHomeClick();
        break;
    }
  }

  private _splitFooterText(text?: string): SEOText {
    if (window.$app.appConfig.prerenderMode) {
      return {
        base: text?.replace(/\[more\]/g, '')?.trim() ?? '',
        extended: '',
      };
    }

    return {
      base: text?.split('[more]')[0]?.trim() ?? '',
      extended: text?.split('[more]')[1]?.trim() ?? '',
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: BasePage;
  }
}
